import { useGetFootprintingCropFootprints, useGetFootprintingDairyFootprints, useGetFootprintingFarmFootprint } from "utils";

import { FarmResponse, FootprintingFormCompletionStats } from "models";

import { paysFootprintingBonus } from "pages/app/home/hero/homeSegment";
import { HomeFootprintingSummary, summariseFootprinting } from "pages/app/home/status-cards/homeFootprintingSummary";

export type HomeFootprintingSummaryResult = {
  summary: HomeFootprintingSummary;
  loading: boolean;
  hasPayouts: boolean;
  /** a footprinting request failed on its first load: the summary would read as "nothing done", so the cards show it as not available */
  unavailable: boolean;
};

/** Footprinting progress of one calendar year, with the same queries as the farm card and the footprinting page. */
export const useHomeFootprintingSummary = (
  farm: FarmResponse | undefined,
  year: number,
  { enabled = true }: { enabled?: boolean } = {}
): HomeFootprintingSummaryResult => {
  const farmProgressQuery = useGetFootprintingFarmFootprint(farm?.id, year, { enabled });
  const cropProgressQueries = useGetFootprintingCropFootprints(farm?.id, [year], farm?.carbonProgram?.farmLevelFootprinting?.programCrops, { enabled });
  const dairyProgressQueries = useGetFootprintingDairyFootprints(farm?.id, [year], { enabled });

  // only answered queries count; the per-assessment bodies are FootprintingFormCompletionStats with a richer footprint
  const formStats: FootprintingFormCompletionStats[] = [...cropProgressQueries, ...dairyProgressQueries].flatMap((query) => (query.data ? [query.data] : []));

  return {
    summary: summariseFootprinting(farmProgressQuery.data, formStats),
    loading: farmProgressQuery.isLoading || cropProgressQueries.some((query) => query.isLoading) || dairyProgressQueries.some((query) => query.isLoading),
    hasPayouts: paysFootprintingBonus(farm?.carbonProgram, year),
    // a failed refetch keeps the cached footprints: only a failed first load makes the summary unavailable
    unavailable:
      farmProgressQuery.isLoadingError ||
      cropProgressQueries.some((query) => query.isLoadingError) ||
      dairyProgressQueries.some((query) => query.isLoadingError),
  };
};
